import React, { Component, ReactNode, ErrorInfo } from 'react';
import { DebugModeEnabledAtom } from '../../store/DebugStore';
import { useAtomValue } from 'jotai';
import { AccountInfoAtom } from '../../store/AccountStore';
import { AccountInfo } from '../../Models/AccountInfo';
import './InkyErrorBoundary.scss';

interface ErrorBoundaryProps {
    fallback?: ReactNode;
    children: ReactNode;
    customErrorHeader?: string;
    debugModeEnabled?: boolean;
    accountInfo?: AccountInfo;
    extraInfo?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
    customErrorHeader?: string;
    AccountInfo?: AccountInfo;
    debugModeEnabled?: boolean;
}

class InternalInkyErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error: error, errorInfo: null };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // Example "componentStack":
        //   in ComponentThatThrows (created by App)
        //   in ErrorBoundary (created by App)
        //   in div (created by App)
        //   in App
        this.setState({ hasError: true, error: error, errorInfo: info });
        logErrorToMyService(error, info.componentStack);
    }

    componentDidUpdate(prevProps: ErrorBoundaryProps) {
        // Check if debugModeEnabled prop has changed
        if (prevProps.debugModeEnabled !== this.props.debugModeEnabled || prevProps.customErrorHeader !== this.props.customErrorHeader) {
            this.setState({
                debugModeEnabled: this.props.debugModeEnabled,
                customErrorHeader: this.props.customErrorHeader,
            });
        }
    }

    getCopyTextString(formatWithMD = false): string {
        if (formatWithMD) {
            return `##### Error Message:
\`\`\`
${this.state.error?.message}
\`\`\`

##### Extra Info:
\`\`\`json
${this.props.extraInfo} 
\`\`\`          

##### Component Stack:
\`\`\`
${this.state.errorInfo?.componentStack}
\`\`\` 

##### System Info:

- User Agent: \`${navigator.userAgent}\` 

- URL: ${window.location.href} 

- Screen Size: \`${window.screen.width}x${window.screen.height}\`

- Window Size: \`${window.innerWidth}x${window.innerHeight}\`

- Time: \`${new Date().toLocaleString()}\`

- Debug Mode Enabled: \`${!!this.state.debugModeEnabled}\`


 ##### Account Info:
 \`\`\`json
 ${JSON.stringify(this.props.accountInfo, null, 2)}
 \`\`\`
 `;
        }

        return `Error Message: ${this.state.error?.message}

Component Stack: ${this.state.errorInfo?.componentStack}

Extra Info: ${this.props.extraInfo} 
                            

 User Agent: ${navigator.userAgent} 

 URL: ${window.location.href} 

 Screen Size: ${window.screen.width}x${window.screen.height} 

 Window Size: ${window.innerWidth}x${window.innerHeight} 

 Time: ${new Date().toLocaleString()} 

 Debug Mode Enabled: ${this.state.debugModeEnabled}
                            

 AccountInfo: ${JSON.stringify(this.props.accountInfo, null, 2)}`;
    }

    renderErrorInfo(): ReactNode {
        return <>
            <div
                className={'inky-error-boundary'}
            >
                <h1>{this.props.customErrorHeader ?? 'Something went wrong.'}</h1>
                <h5>Error Message</h5>
                <textarea style={{ height: '50px', width: '100%' }} disabled={true} value={this.state.error?.message} />
                <h5>Component Stack</h5>
                <textarea style={{ height: '250px', width: '100%' }} disabled={true}
                          value={this.state.errorInfo?.componentStack} />
                {this.props.extraInfo && <>
                    <h5>Extra Info</h5>
                    <textarea style={{ height: '250px', width: '100%' }} disabled={true} value={this.props.extraInfo} />
                </>}
                <button
                    onClick={async () => {
                        try {
                            const text = this.getCopyTextString();
                            await navigator.clipboard.writeText(text);
                        } catch (err) {
                            console.error('Error copying to clipboard, clipboard will only work in secure context, i.e. https or localhost.', err);
                        }
                    }}
                >Copy All Info
                </button>
                <button
                    onClick={async () => {
                        try {
                            const text = this.getCopyTextString(true);
                            await navigator.clipboard.writeText(text);
                        } catch (err) {
                            console.error('Error copying to clipboard, clipboard will only work in secure context, i.e. https or localhost.', err);
                        }
                    }}
                >Copy All Info (With Markdown Formatting)
                </button>
            </div>
        </>;
    }

    render() {
        if (this.state.hasError) {

            if (!!this.state.debugModeEnabled || process.env.REACT_APP_SHOW_ERROR_BOUNDARY === 'true') {
                // You can render any custom fallback UI
                if (this.props.fallback !== undefined) {
                    return this.props.fallback;
                }
                return this.renderErrorInfo();
            }
            return null;
        }

        return this.props.children;
    }
}

// Function to log errors to a service
function logErrorToMyService(error: Error, componentStack: string) {
    // Implement your error logging service here
}

function InkyErrorBoundary({ children, ...restProps }: Omit<ErrorBoundaryProps, 'debugModeEnabled' | 'accountInfo'>) {
    const debugModeEnabled = useAtomValue(DebugModeEnabledAtom);
    const accountInfo = useAtomValue(AccountInfoAtom);
    return <InternalInkyErrorBoundary
        debugModeEnabled={debugModeEnabled}
        accountInfo={accountInfo}
        {...restProps}
    >{children}</InternalInkyErrorBoundary>;
}

export default InkyErrorBoundary;
